import {
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import { AxiosResponse } from 'axios';
import {
    withHeaders,
    instance as axiosInstance,
} from 'core/util/axiosInstance';
import { useAlert, useAuth } from 'core/contexts';
import { commonShowAlert } from 'lib';
import { getDefaultHeaders } from 'core/util/other';
import { RULE_SET } from 'pages/pipelines/pipelines-helper';

const BASE_RULE_URL = '/metadata/datapipeline/rule';

export interface PostRuleProps {
    name: string;
    description: string;
    importSet: string[];
    whenBase64: string;
    thenBase64: string;
    globalSet: string[];
}

interface CreateRuleStepProps {
    postData: PostRuleProps;
    token: string;
}

export interface RuleStepResponse extends PostRuleProps {
    id: string;
}

export interface UpdateRuleStepProps {
    putData: RuleStepResponse;
    token: string;
}

export const getRulesListData = async (
    url: string,
    token: string
): Promise<RuleStepResponse> => {
    const headers = getDefaultHeaders(token);
    const response = await axiosInstance().get(url, withHeaders(headers));
    return response.data;
};

export const useRules = (
    type: string
): UseQueryResult<RuleStepResponse[], Error> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const { getToken } = useAuth();
    const url = BASE_RULE_URL;

    return useQuery('rules', async () => getRulesListData(url, getToken()), {
        enabled: type === RULE_SET,
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to retrieve rule data';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const postRule = (
    props: CreateRuleStepProps
): Promise<AxiosResponse<RuleStepResponse, any>> => {
    const { postData, token } = props;
    const url = BASE_RULE_URL;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useCreateRule = (): UseMutationResult<
    AxiosResponse<RuleStepResponse, any>,
    Error,
    CreateRuleStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postRule, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Rule Created.');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create rule';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const putRule = (
    props: UpdateRuleStepProps
): Promise<AxiosResponse<RuleStepResponse, any>> => {
    const { putData, token } = props;
    const url = `${BASE_RULE_URL}/${putData.id}`;

    return axiosInstance().put(
        url,
        putData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useUpdateRule = (): UseMutationResult<
    AxiosResponse<RuleStepResponse, any>,
    Error,
    UpdateRuleStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(putRule, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Rule Updated');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to update rule';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
